import type { RequestContext } from '@shared/types/RequestContext.ts';
import type { Page } from '@shared/types/Tilelist';
import type { EvergladesProductsApiSuccess, IntentResult, SearchContext } from '@shared/types/types.ts';
import { isBlackFridayIntent, isContextIntent, isInterleavedIntent, isRankedIntent } from '@shared/types/types.ts';
import { insertBlackFridaySliderSlot } from '@tilelist/domain/black-friday-slider/BlackFridaySlider.ts';
import { getGalleryLayout } from '@tilelist/domain/composition/GalleryLayout.ts';
import { getPagination } from '@tilelist/domain/composition/Pagination.ts';
import type { Pagination } from '@tilelist/domain/composition/Pagination.ts';
import { getRankedPaging } from '@tilelist/domain/composition/PaginationMeta.ts';
import {
    enrichSearchProvidedInterleavedProducts,
    interleaveRankedAndSponsoredProducts,
} from '@tilelist/domain/composition/RankedPositioning.ts';
import { appendSimilarProducts } from '@tilelist/domain/composition/SimilarProducts.ts';
import { isComboItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { ProductItem, TileListItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { insertBenefitCinemaSlots } from '@tilelist/domain/feature-visibility/BenefitCinemaSlot.ts';
import { getComboPagination } from '@tilelist/domain/feature-visibility/ComboPagination.ts';
import type { ComboIntentCounts } from '@tilelist/domain/feature-visibility/ComboPagination.ts';
import {
    COMBO_TILE_POSITION,
    findComboItem,
    insertComboItem,
    isComboGroupB,
    isComboSlotReserved,
} from '@tilelist/domain/feature-visibility/ComboTile.ts';
import { insertProductComparisonBanner } from '@tilelist/domain/feature-visibility/ComparisonBanner.ts';
import { extractComparisonId } from '@tilelist/domain/feature-visibility/ComparisonService.ts';
import { insertMASBannerTargets } from '@tilelist/domain/feature-visibility/MASBanner.ts';
import { insertSDABillboard } from '@tilelist/domain/feature-visibility/SDABillboard.ts';
import { composeBaseTrackingLabels } from '@tilelist/domain/tracking/BaseTracking.ts';

type TileListPageCompositionQuery = {
    c?: string;
    l?: 'gp' | 'gl' | 'gq';
};

type PageLayoutCompositionInput = {
    boostedItems?: ProductItem[];
    teaserVariationBoostShouldBeTracked?: boolean;
};

/**
 *
 *
 *
 *
 */
function resolveIntentCounts(intents: IntentResult[]) {
    const interleavedIntent = intents.find(isInterleavedIntent);
    const rankedIntent = intents.find(isRankedIntent);
    const sponsoredIntent = intents.find((i) => i.intent === 'sponsored');
    const primaryIntent = interleavedIntent ?? rankedIntent;
    return {
        rankedCount: interleavedIntent?.count ?? rankedIntent?.count ?? 0,
        sponsoredCount: interleavedIntent ? 0 : (sponsoredIntent?.count ?? 0),
        pageItemCount: interleavedIntent?.products.length,
        displayCount: primaryIntent?.displayCount ?? 0,
        sortOrder: primaryIntent?.meta?.sortOrder,
    };
}

/**
 *
 *
 *
 */
function paginate(page: Page, comboActive: boolean, counts: ComboIntentCounts): Pagination {
    return comboActive ? getComboPagination(counts, page) : getPagination(counts, page);
}

/**
 *
 *
 *
 */
export function buildPageLayout(
    response: EvergladesProductsApiSuccess,
    requestContext?: RequestContext,
    queryParams?: TileListPageCompositionQuery,
    onex?: OnEx,
    compositionInput?: PageLayoutCompositionInput,
): TileListPageLayout {
    const rankedIntent = response.intents?.find(isRankedIntent);
    const interleavedIntent = response.intents?.find(isInterleavedIntent);

    const primaryIntent = interleavedIntent ?? rankedIntent;
    const isFallback = !primaryIntent || primaryIntent.count === 0;
    const searchContext = extractSearchContext(response);
    const comparisonIdOrNull = extractComparisonId(searchContext, queryParams?.c);
    const comparisonId = comparisonIdOrNull || undefined;
    const layout = getGalleryLayout(response.intents, queryParams?.l, onex);

    /**
 *
 *
 *
 *
 */
    const isMobile = requestContext?.isMobile ?? false;

    const context = {
        rule: response.rule ?? '',
        searchContext,
        layout,
    };

    const page = getRankedPaging(response.intents, layout, comparisonId);
    const { rankedCount, sponsoredCount, pageItemCount, displayCount, sortOrder } = resolveIntentCounts(
        response.intents,
    );
    const intentCounts = { rankedCount, sponsoredCount };

    const comboActive = isComboGroupB(onex);
    const { currentPage } = paginate(page, comboActive, intentCounts);

    const tileListItems = composeTileListItems(
        response.intents,
        currentPage,
        comparisonId,
        onex,
        searchContext,
        compositionInput?.boostedItems,
    );

    const comboShown = tileListItems.some(isComboItem);
    const pagination = paginate(page, comboActive, { ...intentCounts, comboShown });

    const tracking = composeBaseTrackingLabels({
        intents: response.intents,
        isMobile,
        searchContext,
        sortOrder,
        currentPage,
        lastPage: pagination.lastPage,
        layout,
        onex,
        comboShown,
        teaserVariationBoostShouldBeTracked: compositionInput?.teaserVariationBoostShouldBeTracked ?? false,
    });

    return {
        tileListItems,
        tracking,
        page,
        isFallback,
        pagination,
        rankedCount,
        sponsoredCount,
        pageItemCount,
        comboShown,
        displayCount,
        comparisonId,
        context,
    };
}

/**
 *
 *
 *
 *
 *
 *
 *
 */
function composeTileListItems(
    intents: IntentResult[],
    currentPage: number,
    comparisonId?: string,
    onex?: OnEx,
    searchContext?: SearchContext,
    boostedItems?: ProductItem[],
): TileListItem[] {
    const interleavedIntent = intents.find(isInterleavedIntent);
    const combo = interleavedIntent ? undefined : findComboItem(intents, searchContext, onex, currentPage);
    const interleavedProducts = interleavedIntent
        ? enrichSearchProvidedInterleavedProducts(interleavedIntent)
        : interleaveRankedAndSponsoredProducts(
              intents,
              (currentPage - 1) * 120,
              combo ? COMBO_TILE_POSITION : undefined,
          );
    const productItems = prependBoostedItems(boostedItems, interleavedProducts);
    const lastItem = productItems.at(-1);
    const lastOffset = lastItem?.localListPosition ?? 0;
    const blackFridaySliderActive = onex?.isToggleActive('REPTILE_1717_BLACK_FRIDAY_SLIDER') ?? false;

    let tileListItems = insertComboItem(productItems, combo);
    tileListItems = insertProductComparisonBanner(tileListItems, comparisonId, onex);
    tileListItems = insertMASBannerTargets(tileListItems);
    tileListItems = insertBlackFridaySliderSlot(
        tileListItems,
        searchContext,
        currentPage,
        blackFridaySliderActive,
        intents.find(isBlackFridayIntent)?.rule,
    );
    tileListItems = insertSDABillboard(tileListItems, searchContext, onex, {
        primeSlotReserved: blackFridaySliderActive || isComboSlotReserved(onex, searchContext, currentPage),
    });
    tileListItems = insertBenefitCinemaSlots(tileListItems, onex);
    tileListItems = appendSimilarProducts(intents, tileListItems, lastOffset);
    return tileListItems;
}

function prependBoostedItems(boostedItems: ProductItem[] | undefined, products: ProductItem[]): ProductItem[] {
    if (!boostedItems || boostedItems.length === 0) return products;
    const offset = boostedItems.length;
    const renumbered = products.map((item) => ({
        ...item,
        localListPosition: item.localListPosition + offset,
        actualListPosition: item.actualListPosition + offset,
    }));
    return [...boostedItems, ...renumbered];
}

function extractSearchContext(response: EvergladesProductsApiSuccess): SearchContext {
    const context = response.intents.find(isContextIntent) || {};
    return {
        rule: response.rule,
        ...context,
    };
}
